<template>
  <Banners
    :is-ultra="true"
    :sticky-top="true"
  >
    <div class="container-wide-eq mx-auto flex flex-row gap-5 p-6">
      <div
        v-if="!isTabletOrMobile"
        class="sticky top-[3.5rem] hidden w-48 flex-col items-start self-start pt-6 md:flex"
      >
        <div class="relative h-full w-[2px] bg-light-gray">
          <div
            id="activeBar"
            class="absolute left-0 z-20 w-[2px] rounded-full bg-dark-gray transition-all duration-500 ease-out"
            style="height: 12px; top: 6px"
          ></div>
        </div>
        <nav class="space-y-2">
          <a
            v-for="(item, index) in tableOfContents"
            :id="'toc-' + item.pageId"
            :key="index"
            :ref="item.pageId"
            class="group relative z-10 flex h-6 cursor-pointer items-center text-xs font-medium text-body-color transition-colors duration-300 hover:text-dark-gray"
            :class="{
              'font-semibold text-dark-gray': activeSection === item.pageId,
            }"
            :data-value="item.pageId"
            :data-index="index"
            @click="navChange($event)"
          >
            <span class="ml-2 whitespace-nowrap text-left uppercase">{{
              item.name
            }}</span>
          </a>
        </nav>
      </div>

      <div
        class="flex flex-col gap-6 py-4 text-left"
        :style="isTabletOrMobile ? 'height: calc(100dvh - 102px)' : ''"
      >
        <div class="text-left">
          <h1 class="mb-2 text-3xl font-medium text-steel-1100">Equipment</h1>
          <p class="text-base font-normal text-steel-800">
            Highway checks your ELD’s VINs and compares them to your Insurance.
            This way, we know which of your vehicles are insured. If a VIN isn’t
            on both the ELD and the Insurance, we’ll show you so that you can
            correct it.
            <a
              href="https://service.highway.com/en/articles/9807670-why-do-i-need-to-provide-the-list-of-vehicles-covered-by-my-insurance-policy"
              target="_blank"
              class="text-sun-700 underline hover:text-sun-800 active:text-sun-900"
              >Learn more</a
            >
          </p>
        </div>
        <EldSectionMessages
          v-if="!loading && displayEquipmentBanner"
          :status="bannerType"
          @submit-coi-request="onSubmitCoiRequest"
          @update-eld-account="onUpdateEldAccount"
        />
        <div
          v-if="!isTabletOrMobile"
          class="flex flex-col gap-10 text-left"
        >
          <div
            id="eld"
            class="toc-section"
          >
            <ELD :loading="loading" />
          </div>
          <div
            id="insurance"
            class="toc-section flex flex-col gap-6"
          >
            <h3
              v-if="
                generalStatus.has_active_coi_on_file ||
                generalStatus.cargo_policy_on_file_and_active
              "
              class="text-xl text-steel-1100"
            >
              Insurances
            </h3>
            <COI :loading="loading" />
          </div>
          <CargoPolicy
            v-if="displayExclusionsSection"
            :loading="loading"
          />
        </div>
        <div v-else>
          <div
            class="flex w-full items-center justify-between border-b border-steel-300"
          >
            <button
              v-for="t in mobileTabs"
              :key="t.key"
              class="relative flex-1 text-center text-sm font-medium font-semibold transition-colors duration-300"
              style="padding-bottom: 6px"
              :class="activeTab === t.key ? 'text-sun-700' : 'text-steel-700'"
              @click="activeTab = t.key"
            >
              {{ t.label }}
              <span
                class="absolute bottom-0 left-1/2 w-full -translate-x-1/2 transform rounded bg-sun-700 transition-all duration-300"
                :class="
                  activeTab === t.key
                    ? 'h-[3px] opacity-100'
                    : 'h-[3px] opacity-0'
                "
              />
            </button>
          </div>

          <template v-if="activeTab === 'eld'">
            <ELD :loading="loading" />
          </template>
          <div v-show="activeTab === 'insurances'">
            <CollapsiblePanel
              title="Certificate of Insurances"
              background-color="bg-white"
            >
              <COI :loading="loading" />
            </CollapsiblePanel>
            <CollapsiblePanel
              title="Exclusions"
              background-color="bg-white"
            >
              <CargoPolicy :loading="loading" />
            </CollapsiblePanel>
          </div>
        </div>
      </div>
    </div>
  </Banners>
</template>

<script setup>
import { computed, onMounted, ref, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import Banners from '@/components/Banners.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import ELD from '@/components/Compliance/ConnectELD.vue';
import COI from '@/components/Compliance/COI.vue';
import CargoPolicy from '@/components/Compliance/CargoPolicy.vue';
import CollapsiblePanel from '@/components/CollapsiblePanel.vue';
import EldSectionMessages from '@/components/EldSectionMessages.vue';

const store = useStore();
const { screenSize } = useScreenSize();

const tableOfContents = ref([
  { name: 'ELD Connections', pageId: 'eld' },
  { name: 'Insurance', pageId: 'insurance' },
]);

const activeTab = ref('eld');
const activeSection = ref('eld');
const activeSectionIndex = ref(0);
const scrolling = ref(false);
const loading = ref(true);

const carrier_id = computed(
  () => store.state.users?.currentUser?.company?.carrier_id
);
const company_id = computed(() => store.state.users?.currentUser?.company?.id);

const bannerType = computed(
  () => store.state.carriers?.carrierVehiclesForCarrier?.eld_status
);

const displayExclusionsSection = computed(() => {
  return store.state.users.featureFlags?.display_exclusions_section?.enabled;
});

const displayEquipmentBanner = computed(() => {
  return store.state.users.featureFlags?.display_equipment_banner?.enabled;
});

onMounted(async () => {
  loading.value = true;
  scrollListen();
  await Promise.all([
    store.dispatch('onboardingPacket/getEldIntegrations', company_id.value),
    store.dispatch('onboardingPacket/getLatestEldProvider', carrier_id.value),
    store.dispatch(
      'onboardingPacket/getEldVehichleInformation',
      carrier_id.value
    ),
    store.dispatch('insurance/getPolicyByCarrier', carrier_id.value),
    store.dispatch('carriers/getCarrierVehiclesForCarrier', {
      carrierId: carrier_id.value,
      page: 1,
    }),
  ]);

  const res = await store.dispatch('onboardingPacket/getEldStatus');
  if ([200, 201, 304].includes(res?.status) && res?.data?.eld_status) {
    store.commit('onboardingPacket/setEldStatus', res.data);
  }
  loading.value = false;
});
onBeforeUnmount(() => {
  window.onscroll = null;
});

// Using the loadboard access endpoint since it provides all required data points for this page.
const generalStatus = computed(
  () => store.state.users.userLoadBoardAccess?.user
);

const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

const mobileTabs = computed(() => {
  return [
    { key: 'eld', label: 'ELD Connection' },
    { key: 'insurances', label: 'Insurances' },
  ];
});

function navChange(event) {
  event.preventDefault();
  scrolling.value = true;

  const newSection = event.currentTarget.getAttribute('data-value');
  const newIndex = event.currentTarget.getAttribute('data-index');

  activeSection.value = newSection;
  activeSectionIndex.value = newIndex;

  scrollToSection(newSection);
}

function scrollToSection(id) {
  const element = document.getElementById(id);
  if (!element) return;

  const offset = element.offsetTop - 10;
  scrollTo(offset, () => {
    scrolling.value = false;
  });
}

function scrollTo(offset, callback) {
  const fixedOffset = offset.toFixed();
  const onScroll = () => {
    if (window.pageYOffset.toFixed() === fixedOffset) {
      window.removeEventListener('scroll', onScroll);
      callback();
    }
  };

  window.addEventListener('scroll', onScroll);
  onScroll();
  window.scrollTo({ top: offset, behavior: 'smooth' });
}

function onSubmitCoiRequest() {
  store.commit('insurance/setDisplayInsuranceModal', Date.now());
}
function onUpdateEldAccount() {
  const dialog = document.getElementById('dialog');
  dialog?.showModal();
}

function scrollListen() {
  const sectionEls = document.querySelectorAll('.toc-section');
  const sections = {};

  sectionEls.forEach((el) => {
    sections[el.id] = el.offsetTop;
  });

  window.onscroll = () => {
    if (!loading.value && !scrolling.value) {
      let scrollPos =
        document.documentElement.scrollTop || document.body.scrollTop;
      scrollPos += 10;

      for (const id in sections) {
        if (sections[id] <= scrollPos) {
          const el = document.getElementById('toc-' + id);
          if (el) {
            activeSection.value = el.getAttribute('data-value');
            activeSectionIndex.value = el.getAttribute('data-index');
          }
        }
      }
    }
  };
}

watch(activeSectionIndex, (newVal) => {
  const activeBar = document.getElementById('activeBar');
  const newTop = newVal * 2.0;
  activeBar.style.transform = `translateY(${newTop}em)`;
});
</script>
